import { useMemo } from "react";
import { useI18n } from "../../../hooks/useI18n";
import "../EngagementMaster.scss";
import { Table } from "../../CommonUIComponents/Table/Table";
import { getHistoryTabColumData } from "./HistoryTabColumData";
import { parseApiDateAsUtc, utcToLocalDateInput } from "../../../util/dateUtil";

function isApiDateValue(value) {
  if (!value) return false;
  const str = String(value).trim();
  return (
    /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?(?:[.,]\d{1,7})?(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(
      str,
    ) ||
    /^\d{2}\/\d{2}\/\d{4}(?:[T ]\d{2}:\d{2}(?::\d{2})?(?:[.,]\d{1,7})?)?$/.test(
      str,
    )
  );
}

function formatHistoryValue(value) {
  if (!value) return "--";
  if (!isApiDateValue(value)) return value;
  return utcToLocalDateInput(value) || String(value);
}

function formatModifiedDateTime(value) {
  if (!value) return "--";
  const date = parseApiDateAsUtc(value);
  if (!date) return String(value);

  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return `${day}-${month}-${year} | ${time}`;
}

function normalizeHistoryRows(data) {
  const apiRows = data?.history || [];

  if (!Array.isArray(apiRows) || apiRows.length === 0) return [];

  return apiRows.map((row, index) => ({
    id: row?.statusHistoryId || row?.id || index + 1,
    action: row?.action || "--",
    oldValue: formatHistoryValue(row?.oldValue),
    newValue: formatHistoryValue(row?.newValue),
    modifiedBy: row?.modifiedBy || "--",
    modifiedDateTime: formatModifiedDateTime(
      row?.modifiedDate || row?.modifiedDateTime,
    ),
    reason: row?.reason || "--",
  }));
}

const HistoryTab = ({ data = {} }) => {
  const { t } = useI18n();

  const colDefs = useMemo(() => getHistoryTabColumData(t), [t]);
  const rowData = useMemo(() => normalizeHistoryRows(data), [data]);

  const rowClassRules = {
    "history-row-even": (params) => params.node.rowIndex % 2 === 0,
    "history-row-odd": (params) => params.node.rowIndex % 2 !== 0,
  };

  const hasData = rowData.length > 0;

  return (
    <div className="history-tab">
      <div className="history-container">
        {hasData ? (
          <Table
            rowData={rowData}
            colDefs={colDefs}
            pagesize={10}
            pagination={false}
            loading={false}
            suppressMovableColumns={true}
            rowClassRules={rowClassRules}
            rowHeight={44}
            headerHeight={44}
          />
        ) : (
          <div className="history-empty-state">
            <p className="history-empty-text">No history available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryTab;
